import { useEffect, useRef, useState } from 'react'
import { Icons } from './Icons'

type IconKey = keyof typeof Icons
type MegaKey = 'casa' | 'business'

// Quale voce di nav è "attiva" (colore cyan). Le pagine segment-aware
// (VoIP, IP Pubblico) passano 'casa'/'business' in base al segmento corrente,
// così su /ip-pubblico la scheda Privati accende Casa e Business accende Ufficio.
export type ActivePage = MegaKey | 'mobile' | 'assistenza' | 'blog' | null

type MegaItem = { icon: IconKey; name: string; sub: string; href: string }
type MegaData = {
  href: string
  label: string
  primary: MegaItem
  overline: string
  items: MegaItem[]
}

// Dati del mega-menu — portati da docs/design-reference (SH_MEGA), con gli href
// mappati alle route React. I servizi accessori segment-aware puntano all'ancora
// di segmento (#privati / #business) letta dalle pagine VoIP e IP Pubblico al mount.
const MEGA: Record<MegaKey, MegaData> = {
  casa: {
    href: '/offerte-casa',
    label: 'Casa',
    primary: { icon: 'house', name: 'Offerte Casa', sub: 'Fibra FTTH, FTTC e FWA per la tua casa', href: '/offerte-casa' },
    overline: 'Servizi accessori',
    items: [
      { icon: 'phone', name: 'VoIP', sub: 'Il tuo numero fisso in qualità HD, sulla fibra', href: '/voip#privati' },
      { icon: 'globe', name: 'IP Pubblico', sub: 'Indirizzo statico per accedere ai dispositivi da remoto', href: '/ip-pubblico#privati' },
      { icon: 'sim', name: 'Mobile', sub: 'SIM con minuti e Giga sulla rete che preferisci', href: '/mobile#privati' },
      { icon: 'wifi', name: 'Wi-Fi Mesh', sub: 'Copertura in ogni stanza, giardino compreso', href: '/wifi-mesh' },
      { icon: 'tools', name: 'Assistenza TOP', sub: 'Interventi tecnici a domicilio sempre inclusi', href: '/assistenza-top#privati' },
    ],
  },
  business: {
    href: '/offerte-business',
    label: 'Ufficio',
    primary: { icon: 'briefcase', name: 'Offerte Ufficio', sub: "FTTH+ garantita, FTTC e FWA per l'azienda", href: '/offerte-business' },
    overline: 'Servizi accessori',
    items: [
      { icon: 'phone', name: 'VoIP', sub: 'Linee telefoniche VoIP per la tua azienda', href: '/voip#business' },
      { icon: 'globe', name: 'IP Pubblico / Subnet', sub: 'IP statico singolo o intere subnet dedicate', href: '/ip-pubblico#business' },
      { icon: 'headset', name: 'Centralino virtuale', sub: 'Centralino cloud: IVR, code, interni ovunque', href: '/centralino-virtuale' },
      { icon: 'sim', name: 'Mobile', sub: 'SIM aziendali con minuti e Giga per il team', href: '/mobile#business' },
      { icon: 'wifi', name: 'Wi-Fi Mesh', sub: 'Copertura per uffici, locali, hotel e spazi esterni', href: '/wifi-mesh' },
      { icon: 'tools', name: 'Assistenza TOP', sub: 'Interventi tecnici in sede sempre inclusi', href: '/assistenza-top#business' },
      { icon: 'router', name: 'Consulenza MikroTik', sub: 'BGP, alta disponibilità e RouterOS con consultant certificato', href: '/consulenza-mikrotik' },
    ],
  },
}

function MegaRow({ item, primary = false }: { item: MegaItem; primary?: boolean }) {
  const Ic = Icons[item.icon]
  return (
    <a className={`mega-item ${primary ? 'is-primary' : ''}`} href={item.href} role="menuitem">
      <span className="mega-ic"><Ic /></span>
      <span className="mega-tx">
        <span className="mega-nm">{item.name}</span>
        <span className="mega-sb">{item.sub}</span>
      </span>
      <span className="mega-go"><Icons.arrow /></span>
    </a>
  )
}

function Mega({ data }: { data: MegaData }) {
  return (
    <div className="mega" role="menu" aria-label={`Menu ${data.label}`}>
      <div className="mega-card">
        <MegaRow item={data.primary} primary />
        <div className="mega-list">
          <span className="mega-overline">{data.overline}</span>
          {data.items.map((it) => (
            <MegaRow key={it.name} item={it} />
          ))}
        </div>
      </div>
    </div>
  )
}

// A livello di modulo (non dentro Header) per evitare il remount dell'intero
// pannello — e quindi la perdita della transizione apri/chiudi — a ogni render.
function NavMega({
  pageKey, isOpen, isActive, onOpen, onClose,
}: {
  pageKey: MegaKey
  isOpen: boolean
  isActive: boolean
  onOpen: (key: MegaKey) => void
  onClose: () => void
}) {
  const data = MEGA[pageKey]
  return (
    <div
      className={`nav-item has-mega ${isOpen ? 'is-open' : ''}`}
      onMouseEnter={() => onOpen(pageKey)}
      onMouseLeave={onClose}
    >
      <a
        href={data.href}
        className={isActive ? 'is-active' : ''}
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        {data.label}
        <span className="caret"><Icons.arrow style={{ width: 12, height: 12, transform: 'rotate(90deg)' }} /></span>
      </a>
      <Mega data={data} />
    </div>
  )
}

export function Header({ activePage = null }: { activePage?: ActivePage } = {}) {
  const [openMenu, setOpenMenu] = useState<MegaKey | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [mobileGroup, setMobileGroup] = useState<MegaKey | null>(activePage === 'business' ? 'business' : activePage === 'casa' ? 'casa' : null)
  const closeTimer = useRef<number | null>(null)

  // hover-intent: apre subito, chiude con 130ms di tolleranza per attraversare il
  // gap di 16px tra trigger e card senza che il pannello sfarfalli.
  const openMega = (key: MegaKey) => {
    if (closeTimer.current) clearTimeout(closeTimer.current)
    setOpenMenu(key)
  }
  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current)
    closeTimer.current = window.setTimeout(() => setOpenMenu(null), 130)
  }
  useEffect(() => () => { if (closeTimer.current) clearTimeout(closeTimer.current) }, [])

  // Body scroll lock mentre il drawer è aperto
  useEffect(() => {
    if (drawerOpen) {
      const prev = document.body.style.overflow
      document.body.style.overflow = 'hidden'
      return () => { document.body.style.overflow = prev }
    }
  }, [drawerOpen])

  const closeDrawer = () => setDrawerOpen(false)

  return (
    <>
      <header className="hdr" data-sezione="menu">
        <div className="hdr-inner">
          <a className="hdr-logo" href="/">
            <img src="/assets/logo-spadhausen-light.svg" alt="Spadhausen" width={190} height={34} />
          </a>
          <nav className="hdr-nav">
            <NavMega pageKey="casa" isOpen={openMenu === 'casa'} isActive={activePage === 'casa'} onOpen={openMega} onClose={scheduleClose} />
            <NavMega pageKey="business" isOpen={openMenu === 'business'} isActive={activePage === 'business'} onOpen={openMega} onClose={scheduleClose} />
            <div className="nav-item"><a href="/mobile" className={activePage === 'mobile' ? 'is-active' : ''}>Mobile</a></div>
            <div className="nav-item"><a href="/blog" className={activePage === 'blog' ? 'is-active' : ''}>Blog</a></div>
            <div className="nav-item"><a href="/assistenza" className={activePage === 'assistenza' ? 'is-active' : ''}>Assistenza</a></div>
          </nav>
          <div className="hdr-actions">
            {/* Area Clienti nascosta finché l'area dedicata non sarà pronta:
            <a href="#" className="hdr-login">Area Clienti</a> */}
            <a href="/verifica-copertura" className="cta-grad cta-sm">
              <Icons.pin style={{ width: 14, height: 14 }} /> Verifica copertura
            </a>
          </div>
        </div>
      </header>

      <header className="hdr-mobile" data-sezione="menu">
        <a href="/" aria-label="Spadhausen home">
          <img src="/assets/logo-spadhausen-light.svg" alt="Spadhausen" width={156} height={28} />
        </a>
        <button
          className="burger"
          type="button"
          aria-label={drawerOpen ? 'Chiudi menu' : 'Apri menu'}
          aria-expanded={drawerOpen}
          onClick={() => setDrawerOpen((v) => !v)}
        >
          {drawerOpen ? <Icons.x /> : <Icons.menu />}
        </button>
      </header>

      {drawerOpen && (
        <div className="mobile-drawer" role="dialog" aria-modal="true">
          <nav className="mobile-drawer-nav">
            {(['casa', 'business'] as MegaKey[]).map((key) => {
              const data = MEGA[key]
              const expanded = mobileGroup === key
              return (
                <div className={`mdr-group ${expanded ? 'is-open' : ''}`} key={key}>
                  <button
                    type="button"
                    className={`mdr-group-head ${activePage === key ? 'is-active' : ''}`}
                    aria-expanded={expanded}
                    onClick={() => setMobileGroup(expanded ? null : key)}
                  >
                    {data.label}
                    <span className="mdr-toggle">
                      {expanded ? <Icons.minus style={{ width: 16, height: 16 }} /> : <Icons.plus style={{ width: 16, height: 16 }} />}
                    </span>
                  </button>
                  {expanded && (
                    <div className="mdr-sub">
                      <a className="mdr-sub-primary" href={data.primary.href} onClick={closeDrawer}>
                        {data.primary.name}
                        <Icons.arrow style={{ width: 15, height: 15, opacity: 0.6 }} />
                      </a>
                      {data.items.map((it) => {
                        const Ic = Icons[it.icon]
                        return (
                          <a className="mdr-sub-item" href={it.href} key={it.name} onClick={closeDrawer}>
                            <span className="mdr-sub-ic"><Ic /></span>
                            {it.name}
                          </a>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}
            <a href="/mobile" className={activePage === 'mobile' ? 'is-active' : ''} onClick={closeDrawer}>
              Mobile
              <Icons.arrow style={{ width: 16, height: 16, opacity: 0.5 }} />
            </a>
            <a href="/blog" className={activePage === 'blog' ? 'is-active' : ''} onClick={closeDrawer}>
              Blog
              <Icons.arrow style={{ width: 16, height: 16, opacity: 0.5 }} />
            </a>
            <a href="/assistenza" onClick={closeDrawer}>
              Assistenza
              <Icons.arrow style={{ width: 16, height: 16, opacity: 0.5 }} />
            </a>
            {/* Area Clienti nascosta finché l'area dedicata non sarà pronta:
            <a href="#" className="mobile-drawer-login" onClick={closeDrawer}>
              Area Clienti
            </a> */}
            <a
              href="/verifica-copertura"
              className="cta-grad"
              style={{ marginTop: 16, justifyContent: 'center' }}
              onClick={closeDrawer}
            >
              <Icons.pin style={{ width: 14, height: 14 }} /> Verifica copertura
            </a>
          </nav>
        </div>
      )}
    </>
  )
}
